import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Eye, EyeOff, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError } from "../../lib/api";
import { mapTokenResponse } from "../../lib/auth-tokens";
import { formatPhoneInput, normalizePhone } from "../../lib/phone";
import { getDeviceId, getAdminDeviceToken, clearAdminDeviceToken, getDeviceName } from "../../lib/device";
import { AuthFooterLink, AuthLink } from "../../layouts/AuthLayout";
import { StatusBanner } from "../../components/ui/StatusBanner";
import { getAndClearRedirectTarget } from "../../lib/redirect";

const botUsername = (import.meta.env.VITE_BOT_USERNAME || "soorgaa_bot").replace(/^@/, "");

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setAuth, isAuthenticated, role } = useAuth();

  // Mode: "login" for standard sign-in, "setup" for newly promoted admins
  const isSetupParam = searchParams.get("setup") === "1";
  const phoneParam = searchParams.get("phone") || "";

  const [mode, setMode] = useState<"login" | "setup">(isSetupParam ? "setup" : "login");
  const [phone, setPhone] = useState(phoneParam ? formatPhoneInput(phoneParam) : "");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Setup mode specific state
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [otp, setOtp] = useState("");
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const formatCountdown = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  if (isAuthenticated && role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  if (isAuthenticated && role === "student") {
    return <Navigate to="/student" replace />;
  }

  function handleBack() {
    if (sessionToken) {
      setSessionToken(null);
      setOtp("");
      setError("");
      setInfoMessage("");
    } else if (mode === "setup" && !isSetupParam) {
      setMode("login");
      setError("");
      setInfoMessage("");
    } else {
      if (window.history.length > 1) navigate(-1);
      else navigate("/auth/login");
    }
  }

  // Regular login with password
  async function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setLoading(true);
    try {
      const deviceId = getDeviceId();
      const deviceToken = getAdminDeviceToken() || undefined;
      const deviceName = getDeviceName();

      const res = await api.adminLogin({
        phone: normalizePhone(phone),
        password,
        device_id: deviceId,
        device_token: deviceToken,
        device_name: deviceName,
      });

      // Auto-detect newly promoted admin needing password setup!
      if (res.flow === "setup_required" || res.pending_setup) {
        setMode("setup");
        if (res.session_token) {
          setSessionToken(res.session_token);
        }
        setInfoMessage(res.message || "Initial admin password setup required. A 6-digit code was sent to your Telegram.");
        setResendCooldown(60);
        return;
      }

      // If recognized trusted device, sign in immediately without OTP!
      if (res.flow === "authenticated" && res.access_token && res.refresh_token) {
        clearAdminDeviceToken(); // trust now lives in an httpOnly cookie
        setAuth(mapTokenResponse(res as any));
        const target = getAndClearRedirectTarget();
        navigate(target || "/admin");
        return;
      }

      // First time on this device or device revoked: proceed to OTP verification
      if (res.session_token) {
        setSessionToken(res.session_token);
      }
      setInfoMessage(res.message);
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid phone or password");
    } finally {
      setLoading(false);
    }
  }

  // OTP submit during standard login
  async function handleOtpSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken) return;
    setError("");
    setLoading(true);
    try {
      const deviceId = getDeviceId();
      const deviceName = getDeviceName();

      const res = await api.adminVerifyOtp(
        sessionToken,
        otp.replace(/\D/g, ""),
        deviceId,
        deviceName,
      );

      clearAdminDeviceToken(); // trust now lives in an httpOnly cookie
      setAuth(mapTokenResponse(res));
      const target = getAndClearRedirectTarget();
      navigate(target || "/admin");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Invalid verification code");
    } finally {
      setLoading(false);
    }
  }

  // Setup: Step 1 - Send OTP to Telegram
  async function handleInitiateSetup(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setInfoMessage("");
    setLoading(true);
    try {
      const res = await api.adminSetupInitiate({
        phone: normalizePhone(phone),
      });
      setSessionToken(res.session_token);
      setInfoMessage(res.message || "Verification code sent to your linked Telegram account.");
      setResendCooldown(60);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Failed to send verification code. Please ensure your account has administrator privileges and Telegram is linked."
      );
    } finally {
      setLoading(false);
    }
  }

  // Setup: Step 2 - Verify OTP & Set Password (directly logs in!)
  async function handleCompleteSetup(e: React.FormEvent) {
    e.preventDefault();
    if (!sessionToken) return;
    if (newPassword.length < 6) {
      setError("Administrator password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await api.adminSetupPassword({
        session_token: sessionToken,
        code: otp.replace(/\D/g, ""),
        password: newPassword,
      });

      // Direct authenticated login!
      if (res.access_token && res.refresh_token) {
        setAuth(mapTokenResponse(res as any));
        navigate("/admin");
        return;
      }

      // Fallback: switch to login mode with success
      setMode("login");
      setSessionToken(null);
      setPassword(newPassword);
      setInfoMessage("Password set successfully! You can now log in.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to set password. Please check your verification code.");
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!sessionToken || resendCooldown > 0) return;
    setError("");
    setLoading(true);
    try {
      const res = await api.resendCode(sessionToken);
      setInfoMessage(res.message);
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not resend verification code");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1.5 text-xs font-benton font-medium text-muted-foreground hover:text-foreground transition group cursor-pointer"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          {sessionToken ? "Change credentials" : mode === "setup" ? "Back to sign in" : "Back"}
        </button>
        {sessionToken && (
          <span className="text-xs font-mono font-medium text-muted-foreground bg-secondary px-2 py-0.5 rounded-xs border border-border">
            +251 {phone}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-1">
        <h2 className="font-display text-2xl font-bold text-foreground">
          {mode === "setup" ? "Set Admin Password" : "Admin sign in"}
        </h2>
        {mode === "setup" && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            First-Time Setup
          </span>
        )}
      </div>

      <p className="font-benton text-muted-foreground text-xs sm:text-sm mb-5 leading-relaxed">
        {mode === "setup"
          ? "Newly promoted administrators: verify via Telegram and choose your administrator password."
          : "Staff and portal administrator credentials with two-factor verification."}
      </p>

      {error && <div id="auth-error" role="alert"><StatusBanner type="error" message={error} className="mb-4" /></div>}
      {infoMessage && !error && (
        <StatusBanner type="info" message={infoMessage} className="mb-4" />
      )}

      {/* MODE 1: STANDARD SIGN IN */}
      {mode === "login" && !sessionToken && (
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Phone number</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 px-3 py-2 surface-flat text-xs w-20 shrink-0 rounded-xs">
                <span>🇪🇹</span><span>+251</span>
              </div>
              <input
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="91 234 5678"
                autoComplete="tel"
                inputMode="tel"
                aria-describedby={error ? "auth-error" : undefined}
                required
                className="flex-1 px-3 py-2 field text-base sm:text-sm rounded-xs"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Password</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="current-password"
                aria-describedby={error ? "auth-error" : undefined}
                className="w-full pl-3 pr-10 py-2.5 field text-base sm:text-sm rounded-xs"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition p-1"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary w-full py-2.5 rounded-xs text-xs sm:text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-xs cursor-pointer"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Continue <ArrowRight size={15} /></>}
          </button>

          {/* Quick link to password setup */}
          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => {
                setMode("setup");
                setError("");
                setInfoMessage("");
              }}
              className="text-xs text-primary hover:underline font-semibold inline-flex items-center gap-1 cursor-pointer"
            >
              <KeyRound size={13} />
              <span>Newly promoted admin? Set your password here</span>
            </button>
          </div>
        </form>
      )}

      {/* STANDARD OTP SUBMIT */}
      {mode === "login" && sessionToken && (
        <form onSubmit={handleOtpSubmit} className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">6-digit Telegram code</label>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
                setOtp(pasted);
              }}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-describedby={error ? "auth-error" : undefined}
              required
              className="w-full text-center text-xl sm:text-2xl tracking-[0.4em] font-mono py-2.5 rounded-xs border border-border outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-input-background"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground mt-1.5">
              <span>Check your linked Telegram messages for the 6-digit code.</span>
              <a
                href={`https://t.me/${botUsername}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium inline-flex items-center gap-0.5 ml-2 shrink-0"
              >
                Open Telegram bot
              </a>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60 shadow-xs cursor-pointer"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Sign in to dashboard <ArrowRight size={15} /></>}
          </button>
          <button
            type="button"
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            className="w-full py-1.5 text-xs font-semibold text-primary disabled:text-muted-foreground hover:underline cursor-pointer"
          >
            {resendCooldown > 0 ? `Resend code in ${formatCountdown(resendCooldown)}` : "Resend code"}
          </button>
        </form>
      )}

      {/* MODE 2: FIRST-TIME PROMOTED ADMIN SETUP */}
      {mode === "setup" && !sessionToken && (
        <form onSubmit={handleInitiateSetup} className="space-y-4">
          <div className="p-3.5 bg-primary/5 border border-primary/20 rounded-xs text-xs text-muted-foreground leading-relaxed">
            Enter your registered phone number. We will dispatch a 6-digit verification code to your linked Telegram account to verify your identity.
          </div>

          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Administrator Phone number</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 px-3 py-2 surface-flat text-xs w-20 shrink-0 rounded-xs">
                <span>🇪🇹</span><span>+251</span>
              </div>
              <input
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                placeholder="91 234 5678"
                autoComplete="tel"
                inputMode="tel"
                required
                className="flex-1 px-3 py-2 field text-base sm:text-sm rounded-xs"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading || !phone}
            className="btn btn-primary w-full py-2.5 rounded-xs text-xs sm:text-sm font-semibold disabled:opacity-60 flex items-center justify-center gap-2 shadow-xs cursor-pointer"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Send Telegram Code <ArrowRight size={15} /></>}
          </button>

          <div className="pt-2 text-center">
            <button
              type="button"
              onClick={() => {
                setMode("login");
                setError("");
                setInfoMessage("");
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition underline cursor-pointer"
            >
              Already have an administrator password? Sign in
            </button>
          </div>
        </form>
      )}

      {/* SETUP STEP 2: ENTER CODE & CHOOSE PASSWORD */}
      {mode === "setup" && sessionToken && (
        <form onSubmit={handleCompleteSetup} className="space-y-4 mt-2">
          <div>
            <label className="block text-xs font-benton font-semibold mb-1">6-digit Telegram code</label>
            <input
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              inputMode="numeric"
              required
              className="w-full text-center text-xl sm:text-2xl tracking-[0.4em] font-mono py-2.5 rounded-xs border border-border outline-none focus:border-primary focus:ring-1 focus:ring-primary bg-input-background"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Sent to your linked Telegram account for verification.
            </p>
          </div>

          <div>
            <label className="block text-xs font-benton font-semibold mb-1">New Administrator Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                className="w-full pl-3 pr-10 py-2.5 field text-base sm:text-sm rounded-xs"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition p-1 cursor-pointer"
              >
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-benton font-semibold mb-1">Confirm Administrator Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat password"
              required
              minLength={6}
              className="w-full pl-3 pr-3 py-2.5 field text-base sm:text-sm rounded-xs"
            />
          </div>

          <button
            type="submit"
            disabled={loading || otp.length < 6 || !newPassword}
            className="w-full py-2.5 btn btn-primary text-xs sm:text-sm font-semibold rounded-xs flex items-center justify-center gap-2 disabled:opacity-60 shadow-xs cursor-pointer"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <>Set Password & Sign In <ArrowRight size={15} /></>}
          </button>

          <button
            type="button"
            onClick={handleResend}
            disabled={loading || resendCooldown > 0}
            className="w-full py-1 text-xs font-semibold text-primary disabled:text-muted-foreground hover:underline cursor-pointer"
          >
            {resendCooldown > 0 ? `Resend code in ${formatCountdown(resendCooldown)}` : "Resend code"}
          </button>
        </form>
      )}

      <AuthFooterLink>
        Student? <AuthLink to="/auth/login">Go to student sign in</AuthLink>
      </AuthFooterLink>
    </div>
  );
}
